import { useGlobalSettingsContext } from 'contexts/GlobalSettingsContext'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import classnames from 'classnames'
import { Button, Spin, Empty } from 'antd';
import { WIDGET_HEIGHT_PER_UNIT } from 'views/Dashboards/CustomDashboardConstants';
import { ErrorBoundary } from "react-error-boundary";
import { WarningIcon } from 'components/Icons';
import { useLocation } from 'react-router';
import useUpdateEffect from 'hooks/useUpdateEffect';
import { getInnerText } from 'components/Vocabulary';
import { useWidgetCardSettingContext } from 'contexts/WidgetCardContext';
import ACAdvancedEllipsisPopover from 'ac-components/ac-ellipsis-popover/ACAdvancedEllipsisPopover.tsx'

const WidgetCard = React.forwardRef(function WidgetCard({ id, title, subtitle, children, className, style, addScroll = false, viewWithoutScrollEffect = false, isDraggableTitle = false, ...props }, ref) {

    const widgetHeightUnit = props["data-grid"]?.h ?? 1;
    const [isInView, setInView] = useState(viewWithoutScrollEffect);
    const contentRef = useRef();
    const globalSettings = useGlobalSettingsContext();
    const titleText = _.isFunction(title) ? title(globalSettings) : title;
    const { isCustomWidgetTable, contentContainerStyle } = useWidgetCardSettingContext();
    const [extrasWidth, setExtrasWidth] = useState(0);
    const widgetSubtitle = subtitle?.trim();

    // scroll event is used to detect the element in veiw but it won't work in nested scroll
    // then used react-in-viewport and it fails when we want to scroll quickly via code for export pdf fearure
    // interval is the only solution to go with
    useEffect(() => {
        let intervalId = -1;
        if (!isInView) {
            intervalId = setInterval(() => {
                if (contentRef.current && isElementInViewport(contentRef.current)) {
                    clearInterval(intervalId);
                    setInView(true);
                }
            }, 1000);
        }
        return () => clearInterval(intervalId);
    }, [isInView]);

    return (
        <div id={id} className={classnames("widget-card", className, { "default-cursor": isDraggableTitle })} style={style} ref={ref}>
            {titleText ? <WidgetCardTitle isInView={isInView} isDraggableTitle={isDraggableTitle} onExtrasWidthChange={setExtrasWidth} hasSubtitle={widgetSubtitle}>{titleText}</WidgetCardTitle> : null}
            {widgetSubtitle ? <WidgetCardSubtitle extrasWidth={extrasWidth}>{subtitle}</WidgetCardSubtitle> : null}
            <div 
                ref={contentRef} 
                className={classnames("widget-card-content", { "widget-card-no-space": isCustomWidgetTable })} 
                style={{ 
                    overflowY: 'auto', 
                    height: isInView ? undefined : ((WIDGET_HEIGHT_PER_UNIT * widgetHeightUnit) - 20),
                    ...contentContainerStyle
                }}
            >
                {isInView
                    ? <ErrorBoundary FallbackComponent={ErrorFallback}>{children}</ErrorBoundary>
                    : null
                }
            </div>
        </div>
    )
});

export default WidgetCard;

WidgetCard.Extras = ({ children, className }) => {
    return (
        <div className={classnames('widget-card-extras', className)}>
            {children}
        </div>
    )
}

function ErrorFallback({ resetErrorBoundary, error }) {
    const location = useLocation();

    // reset the error boundary on url change as the error might have cause by some filters as well
    useUpdateEffect(
      () => resetErrorBoundary(),
      [resetErrorBoundary, location.search]
    );

    // cause Filter is thrown from useFindingFiltersByGlobalFilters if the dashboard filters and widget filters don't match
    const message = error.cause === "Filter" ? error.message : "Sorry, something went wrong.";
    return (
        <Empty
            image={<WarningIcon/>}
            description={<span style={{ fontSize: 14, fontWeight: 500 }}>{message}</span>}
            imageStyle={{fontSize: '65px', color: '#ccc', marginBottom: 0}}
        >
            <Button onClick={resetErrorBoundary} type="primary">Refresh</Button>
        </Empty>
    );
}

export { ErrorFallback as WidgetErrorFallBack };

function WidgetCardTitle({ children, isInView, isDraggableTitle, onExtrasWidthChange, hasSubtitle }){
    const ref = useRef();
    const titleText = useMemo(() => getInnerText(children), [children]);
    const [style, setStyle] = useState({ width: "100%", minHeight: "24px" });
      
    //applying setInterval to catch the latest widget-card-extras reference
    useEffect(() => {
        let intervalCount = 0

            const intervalId = setInterval(() => {
                if (intervalCount < 18) {
                    const widgetExtras = ref.current?.parentNode?.getElementsByClassName("widget-card-extras");
                    if(!_.isEmpty(widgetExtras)){
                        let width = 0;
                        _.forEach(widgetExtras, widget => width += widget.getBoundingClientRect().width );
                        const finalWidth = `calc(100% - ${width + 10}px)`;
                        setStyle(prev => (prev.width === finalWidth ? prev : { ...prev, width: finalWidth }));
                        onExtrasWidthChange?.(width + 10);
                    }
                    intervalCount += 1;
                } else {
                    clearInterval(intervalId);
                }
            }, 400);

            return () => {
                clearInterval(intervalId);
            };
    }, [isInView]);

    return (
      <>
        <div
          className={classnames("widget-card-title", {
            "draggable-title": isDraggableTitle,
            "p-b-sm": !hasSubtitle,
          })}
          ref={ref}
          style={style}
          title={titleText}
        >
          {children}
        </div>
      </>
    );
}

function WidgetCardSubtitle({ children, extrasWidth }) {
    const subtitleStyle = extrasWidth
        ? { width: `calc(100% - ${extrasWidth}px)` }
        : undefined;

    return (
        <div className="widget-card-subtitle" style={subtitleStyle}>
            <ACAdvancedEllipsisPopover>
                {children}
            </ACAdvancedEllipsisPopover>
        </div>
    );
}

WidgetCard.Spinner = ({ height = "100%", children, spinning }) => {
    return (
        <Spin className='d-flex align-items-center justify-content-center' style={{height}} spinning={spinning} wrapperClassName="h-100 widget-card-spinner">
            {children}
        </Spin>
    )
}

export const WidgetCardSpinner = WidgetCard.Spinner;

export function isElementInViewport(el) {
    const rect = el.getBoundingClientRect();
    // We are only checking that the widget is not hidden below due to page scroll
    return rect.top <= (window.innerHeight || document.documentElement.clientHeight);
}